import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OwnerService } from '@/api/services/servicesApi.types';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useOwnServicesQuery } from '@/hooks/useOwnServicesQuery';
import { buildTherapyTypeTranslationKey } from '@/constants/appointmentOptions';
import { formatPriceFromMinorUnits } from '@/utils/moneyFormatting';
import { resolveLocalizedName } from '@/components/practitioner/TreatmentLibrary/localizedName';

export function ServicesSection() {
  const { t, i18n } = useTranslation('services');
  const query = useOwnServicesQuery(true);
  const services = query.data === undefined ? [] : query.data;

  if (query.isLoading === true) {
    return (
      <Card as="section">
        <div className="py-6 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      </Card>
    );
  }

  if (query.isError === true || services.length === 0) {
    return null;
  }

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="Tag" />
        </span>
        <h2 className="text-sm font-bold text-zooindigo">{t('portal.title')}</h2>
      </div>
      <div className="space-y-3">
        {services.map(function renderService(service) {
          return <OwnerServiceRow key={service.id} service={service} language={i18n.language} />;
        })}
      </div>
    </Card>
  );
}

interface OwnerServiceRowProps {
  service: OwnerService;
  language: string;
}

function OwnerServiceRow({ service, language }: OwnerServiceRowProps) {
  const { t } = useTranslation('services');
  const [isPreparationOpen, setIsPreparationOpen] = useState(false);
  const localizedName = resolveLocalizedName(service.nameEn, service.namePl);
  const localizedDescription = resolveLocalizedName(
    service.descriptionEn === null ? '' : service.descriptionEn,
    service.descriptionPl === null ? '' : service.descriptionPl
  );
  const localizedPreparation = resolveLocalizedName(
    service.preparationEn === null ? '' : service.preparationEn,
    service.preparationPl === null ? '' : service.preparationPl
  );
  const formattedPrice = formatPriceFromMinorUnits(
    service.priceMinorUnits,
    service.currency,
    language
  );

  return (
    <article className="p-3 border border-zooborder rounded-xl">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-bold text-zooindigo truncate">{localizedName}</div>
          {service.therapyType === null ? null : (
            <Badge uppercase size="sm" className="mt-1 px-2 py-0.5">
              {t(buildTherapyTypeTranslationKey(service.therapyType))}
            </Badge>
          )}
        </div>
        <div className="text-[13px] font-bold text-zooprimary shrink-0">
          {formattedPrice === null ? t('priceOnRequest') : formattedPrice}
        </div>
      </div>

      {localizedDescription.length === 0 ? null : (
        <p className="text-[13px] text-zooindigo whitespace-pre-line mt-2">
          {localizedDescription}
        </p>
      )}

      {localizedPreparation.length === 0 ? null : (
        <div className="mt-2">
          <button
            type="button"
            onClick={function toggle() {
              setIsPreparationOpen(function invert(previous) {
                return previous === false;
              });
            }}
            className="text-[12px] font-semibold text-zooviolet flex items-center gap-1"
          >
            <Icon name={isPreparationOpen ? 'ChevronDown' : 'ChevronRight'} />
            {t('portal.preparationLabel')}
          </button>
          {isPreparationOpen === true ? (
            <p className="text-[13px] text-zoomuted whitespace-pre-line mt-1.5">
              {localizedPreparation}
            </p>
          ) : null}
        </div>
      )}
    </article>
  );
}
